import React, { useEffect, useCallback } from "react";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import Navigation from "./components/Navigation/Navigation";
import SiteHeader from "./components/SiteHeader/SiteHeader";
import Loader from "./components/Loader/Loader";
import Footer from "./components/Footer/Footer";
import CreateAbsenceEntry from "./components/CreateAbsenceEntry/CreateAbsenceEntry";
import GatewayUrl from "./components/Gateway/Gateway";
import LearningAndAbsenceTimeOverview from "./components/LearningAndAbsenceTimeOverview/LearningAndAbsenceTimeOverview";
import Faq from "./components/Faq/Faq";
import { fetchOptions } from './hooks/fetchOptions'
import { RootState } from './store/store'
import { useSelector, useDispatch } from 'react-redux'
import { setServerDate } from './store/features/serverDate'
import { setToken, setFirstName, setLastName, setStartDate, setEndDate, setUuidOffer, setLoading } from './store/features/participant'
import { setReasons, setTimes } from './store/features/absences'
import { setMediums } from './store/features/learning'
import { handleLoadingError, logoutUser, setSessionInvalid } from './store/features/session'
import { useMsal } from "@azure/msal-react";
import { loginRequest, msalConfig } from "./auth/authConfig";
import Welcome from './components/Welcome/Welcome'
import EditAbsenceTimeContainer from "./components/EditAbsenceTime/EditAbsenceTime";

import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

const App = () => {
  const dispatch = useDispatch()

  const LoadingParticipantData: boolean = useSelector((state: RootState) => state.participant.loading)
  const token: string = useSelector((state: RootState) => state.participant.token)

  const getSystemData = useCallback(async () => {
    const payload: object = {
      method: "getSystemDataNew",
      sessionId: token,
    }

    await fetch(GatewayUrl, fetchOptions(payload))
      .then((response) => response.json())
      .then((result: any) => {
        if (result.error) {
          dispatch(setSessionInvalid(true))
          dispatch(logoutUser())
        } else if (Object.keys(result).length === 0) {
          dispatch(handleLoadingError("lzk#getSystemData"));
        } else {
          dispatch(setServerDate(result[0].serverDate))
          dispatch(setReasons(result[0].absentReasons))
          dispatch(setTimes(result[0].timeOptions));
          dispatch(setMediums(result[0].learningMediums))
        }
      })
      .catch(() => dispatch(handleLoadingError("lzk#getSystemData")));
  }, [dispatch, token]);

  const getUserData = useCallback(async () => {
    dispatch(setLoading(true))

    const payload: object = {
      method: "getUserDataParticipant",
      sessionId: token,
    }

    await fetch(GatewayUrl, fetchOptions(payload))
      .then((response) => response.json())
      .then((result: any) => {
        if (Object.keys(result).length === 0) {
          dispatch(handleLoadingError("lzk#getUserDataZero"));
        } else if (result.error) {
          dispatch(setSessionInvalid(true))
          dispatch(logoutUser())
        } else {
          dispatch(setFirstName(result.firstName));
          dispatch(setLastName(result.lastName));
          dispatch(setStartDate(result.startDate));
          dispatch(setEndDate(result.endDate));
          dispatch(setUuidOffer(result.uuidOffer));
          dispatch(setLoading(false))
        }
      })
      .catch(() => dispatch(handleLoadingError("lzk#getUserDataError")));
  }, [dispatch, token]);

  const asyncLoad = useCallback(async () => {
    await getSystemData()
    await getUserData();
  }, [getSystemData, getUserData])

  useEffect(() => {
    if (token.length > 0) {
      asyncLoad()
    }
  }, [asyncLoad, token.length]);

  const { instance, accounts } = useMsal();

  // Token
  useEffect(() => {
    const fetchToken = async () => {
      await instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
          // scopes: ["User.Read"],
          redirectUri: msalConfig.auth.redirectUri,
        })
        .then(async (response: any) => {
          dispatch(setToken(response.accessToken))
        })
        .catch(() => {
          instance.logoutRedirect({
            account: accounts[0],
            postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
          });
        });
    };

    fetchToken();
  }, [accounts, dispatch, instance]);

  if (LoadingParticipantData) {
    return <Loader></Loader>;
  } else {
    return (
      <BrowserRouter>
        <section className="sitewrapper">
          <Navigation></Navigation>

          <Switch>
            <Route exact path="/">
              <Welcome />
              <CreateAbsenceEntry />
              <LearningAndAbsenceTimeOverview />
            </Route>

            <Route exact path="/faq">
              <SiteHeader showText={"FAQ für Teilnehmende"}></SiteHeader>
              <Faq></Faq>
            </Route>

            <Route exact path="/abwesenheit-bearbeiten">
              <SiteHeader showText={"Abwesenheit bearbeiten"}></SiteHeader>
              <EditAbsenceTimeContainer />
            </Route>

            <Redirect from="*" to="/" />
          </Switch>
        </section>
        <Switch>
          <Footer></Footer>
        </Switch>
      </BrowserRouter>
    );
  }
};

export default App;
